import { ApiResponse } from '../../../services/apiService';
import { React, useState, useParams, useApi, Dialog, DialogActions, CloseIcon, DialogContent, DialogTitle, Divider, IconButton, Grid2, Typography, Box, useEffect } from '../../../utils/globalImports';
import { CustomRadioButton, CustomButton, CustomTextField, CustomDropdown, validationMessages, ProposalAddInstructedFeeStage } from '../../../utils/globalImports';

interface ModalProps {
    open: boolean;  // Prop to determine if the modal is open
    onClose: () => void;  // Function to handle modal close
    afterSubmit: () => void;  // Function to handle modal close
}

interface Option {
    value: string | number;
    text: string;
    instructionValue: number | string;
}

interface IntructionTypes {
    id: number;
    description: string;
    active: boolean;
    instructionValue: number;
}

interface ProposalFeeStage {
    id: number;
    refrenceNumber: string;
    description: string;
    unitPrice: number;
    unitPricePer: string;
    status?: number;
    completionNotes: string;
    proposalValue: number;
    instructedValue: number;
    quantity: number;
    feeStageNumber: string;
    instructedQuantity: number;
    instructionReference: string;
}

const ProposalInstructedFeeStage: React.FC<ModalProps> = ({ open, onClose, afterSubmit }) => {

    const [instructionOptions, setInstructionOptions] = useState<Option[]>([]);
    const [feeStageOption, setFeeStageOption] = useState<any[]>([]);
    const [modal, setModal] = useState(false);
    const [preInstructionValue, setPreInstructionValue] = useState<number>(0);
    const { execute } = useApi();


    const { proposalId } = useParams<{ proposalId?: string }>(); // Get proposalId from URL
    const { projectId } = useParams<{ projectId?: string }>(); // Get projectId from URL

    // Form data state
    const [formData, setFormData] = useState({
        intructionType: -1,
        poAmount:0,
        intructionValue: 0,
        instructionReference: '',
    });

    const [errors, setErrors] = useState({
        intructionType: '',
        poAmount:'',
        intructionValue: '',
        instructionReference: ''
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : type === "radio" ? value === "true" : value,
        }));
    };

    useEffect(() => {
        if (open) {
            setFormData({
                intructionType: -1,
                poAmount:0,
                intructionValue: 0,
                instructionReference: ''
            });

            setErrors({
                intructionType: '',
                poAmount:'',
                intructionValue: '',
                instructionReference: ''
            });
            fetchInstructionType();
        }

    }, [open])


    // fetch Instruction
    const fetchInstructionType = async () => {
        const { data } = await execute<IntructionTypes[]>('get', `/Proposal/GetIntructionTypes/${proposalId}`);
        if (data && Array.isArray(data)) {
            const instructionOptions = data
                .filter((instruction: any) => instruction.active)
                .map((instruction: any) => ({
                    value: instruction.id,  // Ensure `id` exists in the response
                    text: instruction.description, // Ensure `name` exists in the response
                    instructionValue: instruction.instructionValue
                }));

            setInstructionOptions(instructionOptions);
            setPreInstructionValue(data[0].instructionValue)
        }
    }

    const fetchProposalfeeStages = async () => {
        const { data } = await execute<ProposalFeeStage[]>('get', `/Proposal/feeStages/${proposalId}/${projectId}`);
        if (data) {
            const activeFeeStage = data
                .filter((feeStage: any) => feeStage.status)
                .map((feeStage) => {

                    const { id, refrenceNumber, feeStageNumber, instructedQuantity, description, unitPrice, unitPricePer, proposalValue, completionNotes, instructedValue, quantity } = feeStage;

                    return {
                        id,
                        feeStageNumber,
                        unitPrice,
                        unitPricePer,
                        quantity,
                        "Price": `£ ${unitPrice}, ${unitPricePer}`,
                        description,
                        proposalValue,
                        completionNotes,
                        instructedValue,
                        instructedQuantity
                    }
                })

            if (validateInstructedValue(activeFeeStage)) {
                setFeeStageOption(activeFeeStage)
                setModal(true);
                onClose();
            }
        }
    };

    const IsReferenceExist = async () => {
        return await execute<boolean>('get', `/Proposal/IsInstructedReferenceExists/${proposalId}/${formData.instructionReference}`);
    };


    const validateInstructedValue = (rows: any): boolean => {

        let isValid = true;

        const errors = { intructionType: '',poAmount: '', intructionValue: '', instructionReference: '' };
        const totalValue = rows.reduce((sum: number, row: { proposalValue: any; }) => {
            const val = Number(row.proposalValue);
            return !isNaN(val) ? sum + val : sum;
        }, 0);

        if (formData.intructionValue > totalValue || formData.intructionValue < preInstructionValue) {
            errors.intructionValue = validationMessages.proposal_InstructedValueCheck("Instruction value", totalValue, preInstructionValue);
            isValid = false;
        }

        setErrors(errors);
        return isValid;
    }

    // validate form field
    const validate = async () => {

        let isValid = true;
        const errors = { intructionType: '',poAmount: '', intructionValue: '', instructionReference: '' };

        if (formData.intructionType == -1) {
            errors.intructionType = validationMessages.common_required("Intruction type");
            isValid = false;
        }

        if (!formData.intructionValue) {
            errors.intructionValue = validationMessages.common_required("Instruction value");
            isValid = false;
        }
        // else if (Number(formData.intructionValue) <= 0) {
        //     errors.intructionValue = validationMessages.common_negativeNumber("Instruction value");
        //     isValid = false;
        // }

        if (!formData.instructionReference || !formData.instructionReference.trim()) {
            errors.instructionReference = validationMessages.common_required("Instruction reference");
            isValid = false;
        }

        if (isValid) {
            const referenceExists = await IsReferenceExist(); // make sure to pass `reference`
            if (referenceExists.data) {
                errors.instructionReference = validationMessages.proposal_InstructedRefernceExistText;
                isValid = false;
            }
        }

        setErrors(errors);
        return isValid;
    };

    const handleNext = async (e: React.FormEvent) => {
        e.preventDefault();
        if (await validate()) {
            await fetchProposalfeeStages();
        }
    };

    const handleAfterSubmit = () => {
        setModal(false);           // Close child-child modal
        afterSubmit();             // Notify parent
    };

    return (
        <>
            {/* Modal Dialog */}
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle
                    sx={{
                        position: 'relative',
                        color: '#1e66ab',
                        alignItems: 'center',
                        display: 'flex',
                        justifyContent: 'center',
                        mb: 0,
                        fontWeight: 500,
                        fontSize: '24px'
                    }}>
                    Select Instructed Fee Stages

                    <IconButton
                        color="inherit"
                        onClick={onClose}
                        sx={{
                            position: 'absolute',
                            right: 8, // Position the button at the right
                            top: 8,   // Position the button at the top
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <Divider></Divider>

                <DialogContent>

                    <CustomDropdown
                        value={formData.intructionType}
                        onChange={(event) => {
                            const selectedValue = Number(event.target.value);
                            const selectedOption = instructionOptions.find(opt => opt.value === selectedValue);

                            if (selectedOption) {
                                setFormData({
                                    ...formData,
                                    intructionType: Number(selectedOption.value),
                                    intructionValue: Number(selectedOption.instructionValue)
                                });
                            }
                        }}
                        label="Intruction Type"
                        options={instructionOptions}
                        requiredLabel={true}
                        sx={{ mb: 1, height: '35px', fontSize: '12px' }}
                    />
                    {errors.intructionType && (
                        <Typography className="validationMessage" role="alert">
                            {errors.intructionType}
                        </Typography>
                    )}
                    {(formData.intructionType===7 || formData.intructionType===8) &&(
                    <CustomTextField
                        label="PO Amount"
                        name="poAmount"
                        value={formData.poAmount}
                        onChange={handleChange}
                        type='number'
                        sx={{ mb: 1 }}
                        required={true}
                    />
                    )}
                    <CustomTextField
                        label="Instruction Value"
                        name="intructionValue"
                        value={formData.intructionValue}
                        onChange={handleChange}
                        type='number'
                        sx={{ mb: 1 }}
                        required={true}
                    />
                    {errors.intructionValue && (
                        <Typography className="validationMessage" role="alert">
                            {errors.intructionValue}
                        </Typography>
                    )}

                    <CustomTextField
                        label="Instruction Reference"
                        name="instructionReference"
                        value={formData.instructionReference}
                        onChange={handleChange}
                        sx={{ mb: 1 }}
                        required={true}
                    />
                    {errors.instructionReference && (
                        <Typography className="validationMessage" role="alert">
                            {errors.instructionReference}
                        </Typography>
                    )}

                </DialogContent>

                <DialogActions sx={{ mb: 3 }}>
                    <CustomButton
                        capsule
                        sx={{
                            mt: -1,
                            mb: 2,
                            width: '25%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            textAlign: 'center',
                            margin: '0 auto', // Centers horizontally if parent has `display: block`
                            textTransform: 'none'
                        }}
                        onClick={handleNext}

                    >
                        Next
                    </CustomButton>

                </DialogActions>
            </Dialog>

            <ProposalAddInstructedFeeStage
                open={modal}
                onClose={() => setModal(false)}
                intructionData={formData}
                feeStageData={feeStageOption}
                afterSubmit={handleAfterSubmit}
            >

            </ProposalAddInstructedFeeStage>

        </>
    );
}

export default ProposalInstructedFeeStage;